/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type {
  FilterFieldDefinition,
  FilterFieldTypes,
} from '@shared/types';
import {
  EqualIcon,
  NotEqualIcon,
  GreaterThanIcon,
  LessThanIcon,
  GreaterThanOrEqualIcon,
  LessThanOrEqualIcon,
  BracketsContainIcon,
} from './filter-operator-icons';
import i18n from '@shared/i18n';

/**
 * Translates a key in the `common` namespace via the shared i18n instance.
 *
 * @param key The translation key.
 * @returns The translated string.
 */
const t = (key: string) => i18n.t(key, { ns: 'common' });

export const filterFieldsMap = {
  equals: {
    fieldType: "equals",
    get label() { return t('filters.operators.equalsTo'); },
    urlOperator: "",
    icon: EqualIcon,
  },
  different: {
    fieldType: "different",
    get label() { return t('filters.operators.differentFrom'); },
    urlOperator: "ne",
    icon: NotEqualIcon,
  },
  like: {
    fieldType: "like",
    get label() { return t('filters.operators.like'); },
    urlOperator: "like",
    icon: BracketsContainIcon,
  },
  greater: {
    fieldType: "greater",
    get label() { return t('filters.operators.greaterThan'); },
    urlOperator: "gte",
    icon: GreaterThanIcon,
  },
  lower: {
    fieldType: "lower",
    get label() { return t('filters.operators.lowerThan'); },
    urlOperator: "lte",
    icon: LessThanIcon,
  },
  "greater-or-equals": {
    fieldType: "greater-or-equals",
    get label() { return t('filters.operators.greaterOrEquals'); },
    urlOperator: "gte",
    icon: GreaterThanOrEqualIcon,
  },
  "lower-or-equals": {
    fieldType: "lower-or-equals",
    get label() { return t('filters.operators.lowerOrEquals'); },
    urlOperator: "lte",
    icon: LessThanOrEqualIcon,
  },
} satisfies Record<FilterFieldTypes, FilterFieldDefinition>;
